/**
 * Prompt image tags: the composer lets a user reference an uploaded photo
 * inside the prompt as `@image1`, `@image2`, … and say how that exact photo
 * is used ("@image1 is the singer, keep her outfit", "@image2 is the
 * location", "@image3 is the mood only"). Before this, an "@name" in the
 * prompt was plain prose — the planner guessed which photo (if any) a named
 * person meant, and guessed wrong (DJ Gebo "UN POCO LOCO", Sep 2 2026: the
 * uploaded woman became an unnamed "dance partner" while "@maya" was drawn
 * as a text-only stranger).
 *
 * Pure helpers shared by the web composer (chip numbering, @-autocomplete)
 * and the server (slot resolution, planner legend). No I/O.
 */

export const PROMPT_IMAGE_TAG_PREFIX = "image";
/** Hard cap on tags a client may send; mirrors the reference cap. */
export const MAX_PROMPT_IMAGE_TAGS = 12;

const TAG_RE = /^image([1-9]\d?)$/;
/** Global, case-insensitive: every `@imageN` mention in a prompt. The tag
 *  must end at a word boundary so `@image12` never matches as `@image1`. */
const MENTION_RE = /@(image[1-9]\d?)(?![\w])/gi;

export function promptImageTagFor(index1: number): string {
  return `${PROMPT_IMAGE_TAG_PREFIX}${index1}`;
}

/** Lower-cased tag without the leading "@", or undefined when malformed. */
export function normalizePromptImageTag(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const tag = raw.trim().replace(/^@/, "").toLowerCase();
  return TAG_RE.test(tag) ? tag : undefined;
}

/** 1-based number inside a valid tag (`image3` → 3). */
export function promptImageTagNumber(tag: string): number | undefined {
  const match = TAG_RE.exec(tag);
  return match ? Number(match[1]) : undefined;
}

/** Unique tags mentioned in the prompt, in first-mention order (normalized). */
export function mentionedPromptImageTags(
  prompt: string | null | undefined,
): string[] {
  if (!prompt) return [];
  const out: string[] = [];
  const re = new RegExp(MENTION_RE.source, MENTION_RE.flags);
  let match: RegExpExecArray | null;
  while ((match = re.exec(prompt)) !== null) {
    const tag = match[1].toLowerCase();
    if (!out.includes(tag)) out.push(tag);
  }
  return out;
}

export function promptMentionsImageTag(
  prompt: string | null | undefined,
  tag: string,
): boolean {
  const normalized = normalizePromptImageTag(tag);
  if (!normalized || !prompt) return false;
  return mentionedPromptImageTags(prompt).includes(normalized);
}

const MAX_INSTRUCTIONS_PER_TAG = 4;
const MAX_INSTRUCTION_CHARS = 400;

/**
 * The user's own sentences about one tag — every sentence (or line) of the
 * prompt that mentions `@tag`, trimmed, de-duplicated, capped. These are
 * quoted verbatim to the planners so a paraphrased intent can never lose
 * what the user said about a specific photo.
 */
export function promptImageTagInstructions(
  prompt: string | null | undefined,
  tag: string,
): string[] {
  const normalized = normalizePromptImageTag(tag);
  if (!normalized || !prompt) return [];
  const mention = new RegExp(`@${normalized}(?![\\w])`, "i");
  const out: string[] = [];
  // Sentence-ish units: runs of text up to a terminator, split on newlines
  // too. No lookbehind so the same code runs in older mobile browsers.
  const units = prompt.match(/[^.!?\n]+[.!?]*/g) ?? [];
  for (const unit of units) {
    const sentence = unit.trim();
    if (!sentence || !mention.test(sentence)) continue;
    const clipped =
      sentence.length > MAX_INSTRUCTION_CHARS
        ? `${sentence.slice(0, MAX_INSTRUCTION_CHARS - 1)}…`
        : sentence;
    if (!out.includes(clipped)) out.push(clipped);
    if (out.length >= MAX_INSTRUCTIONS_PER_TAG) break;
  }
  return out;
}

/**
 * Give every reference a stable tag. Existing valid tags are kept (so a
 * photo the user already wrote about in the prompt never gets renumbered
 * when another is added, removed, or promoted to lead); missing ones take
 * the lowest unused number in list order.
 */
export function assignPromptImageTags<T extends { tag?: string }>(
  list: readonly T[],
): T[] {
  const used = new Set<string>();
  for (const item of list) {
    const tag = normalizePromptImageTag(item.tag);
    if (tag && !used.has(tag)) used.add(tag);
  }
  let next = 1;
  const nextFree = (): string => {
    while (used.has(promptImageTagFor(next))) next++;
    const tag = promptImageTagFor(next);
    used.add(tag);
    return tag;
  };
  const seen = new Set<string>();
  return list.map((item) => {
    const tag = normalizePromptImageTag(item.tag);
    if (tag && !seen.has(tag)) {
      seen.add(tag);
      return tag === item.tag ? item : { ...item, tag };
    }
    const fresh = nextFree();
    seen.add(fresh);
    return { ...item, tag: fresh };
  });
}

/**
 * Autocomplete trigger: when the caret sits right after `@` + an optional
 * partial word, return that partial (lower-cased) and where the `@` starts.
 * Returns undefined when the caret is not inside an @-token.
 */
export function promptImageTagQueryAtCaret(
  text: string,
  caret: number,
): { start: number; query: string } | undefined {
  const before = text.slice(0, Math.max(0, caret));
  const match = /(^|[^\w@])@([a-z0-9]*)$/i.exec(before);
  if (!match) return undefined;
  const start = before.length - match[2].length - 1;
  return { start, query: match[2].toLowerCase() };
}
